'use client';

import * as React from 'react';
import { useFirestore } from '@/firebase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Images,
  Search,
  Folder,
  ArrowLeft,
  Check,
  Link as LinkIcon,
  X,
  Upload,
  Loader2,
} from 'lucide-react';
import Image from 'next/image';
import { getHybridRootAlbums, getHybridSubAlbums, getHybridAlbumMedia, enrichAlbumsWithCovers, type HybridAlbum, type HybridMediaItem } from '@/lib/gallery-hybrid';
import { uploadImage, prepareImageForUpload, fileToDataURL } from '@/lib/image-upload';

export interface GalleryImagePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (imageUrl: string) => void;
  currentValue?: string;
  title?: string;
  description?: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
  source: 'firebase' | 'photonlink';
  photonlinkId?: string;
}

/**
 * Gallery Image Picker Dialog
 * Allows users to select an image from the gallery, upload a new image, or enter a URL manually.
 */
export function GalleryImagePicker({
  open,
  onOpenChange,
  onSelect,
  currentValue,
  title = 'Choose Image',
  description = 'Select an image from the gallery, upload a new one, or enter a URL',
}: GalleryImagePickerProps) {
  const firestore = useFirestore();
  const [tab, setTab] = React.useState<'gallery' | 'upload' | 'url'>('gallery');
  const [urlInput, setUrlInput] = React.useState(currentValue || '');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedImage, setSelectedImage] = React.useState<string | null>(currentValue || null);
  const [selectedImageTitle, setSelectedImageTitle] = React.useState<string>('');

  // Upload state
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [uploadPreview, setUploadPreview] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Navigation state
  const [breadcrumbTrail, setBreadcrumbTrail] = React.useState<BreadcrumbItem[]>([]);
  const currentAlbum = breadcrumbTrail[breadcrumbTrail.length - 1];

  // Data state
  const [albums, setAlbums] = React.useState<HybridAlbum[]>([]);
  const [media, setMedia] = React.useState<HybridMediaItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedImage(currentValue || null);
      setSelectedImageTitle('');
      setUrlInput(currentValue || '');
      setBreadcrumbTrail([]);
      setSearchQuery('');
      setUploadPreview(null);
      setUploadError(null);
      setUploadProgress(0);
    }
  }, [open, currentValue]);

  // Fetch root albums on mount
  React.useEffect(() => {
    if (!firestore || !open) return;

    const fetchRootAlbums = async () => {
      try {
        setLoading(true);
        const rootAlbums = await getHybridRootAlbums(firestore);
        const enrichedAlbums = await enrichAlbumsWithCovers(firestore, rootAlbums);
        setAlbums(enrichedAlbums);
        setMedia([]);
      } catch (error) {
        console.error('Error fetching albums:', error);
      } finally {
        setLoading(false);
      }
    };

    if (breadcrumbTrail.length === 0) {
      fetchRootAlbums();
    }
  }, [firestore, open, breadcrumbTrail.length]);

  // Fetch album content when navigating
  React.useEffect(() => {
    if (!firestore || !currentAlbum) return;

    const fetchAlbumContent = async () => {
      try {
        setLoading(true);
        const [subAlbums, albumMedia] = await Promise.all([
          getHybridSubAlbums(firestore, currentAlbum.id, currentAlbum.source, currentAlbum.photonlinkId),
          getHybridAlbumMedia(firestore, currentAlbum.id, currentAlbum.source, currentAlbum.photonlinkId),
        ]);

        const enrichedSubAlbums = await enrichAlbumsWithCovers(firestore, subAlbums);
        setAlbums(enrichedSubAlbums);
        // Filter to only show photos
        setMedia(albumMedia.filter(m => m.type === 'photo'));
      } catch (error) {
        console.error('Error fetching album content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlbumContent();
  }, [firestore, currentAlbum]);

  const handleAlbumClick = (album: HybridAlbum) => {
    setBreadcrumbTrail(prev => [...prev, {
      id: album.id,
      name: album.name,
      source: album.source,
      photonlinkId: album.photonlinkId,
    }]);
  };

  const handleBack = () => {
    setBreadcrumbTrail(prev => prev.slice(0, -1));
  };

  const handleImageSelect = (imageUrl: string, imageTitle: string) => {
    setSelectedImage(imageUrl);
    setSelectedImageTitle(imageTitle);
  };

  const handleClearSelection = () => {
    setSelectedImage(null);
    setSelectedImageTitle('');
  };

  const handleConfirm = () => {
    if (tab === 'url' && urlInput) {
      onSelect(urlInput);
    } else if (selectedImage) {
      onSelect(selectedImage);
    }
    onOpenChange(false);
  };

  // Handle file selection for upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    await processFileUpload(file);
  };

  // Handle drag and drop
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      await processFileUpload(file);
    } else {
      setUploadError('Please drop an image file');
    }
  };

  // Process file upload
  const processFileUpload = async (file: File) => {
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Show preview
      const preview = await fileToDataURL(file);
      setUploadPreview(preview);

      // Prepare image (resize if needed)
      setUploading(true);
      const preparedFile = await prepareImageForUpload(file, 1920, 1080, 0.9);

      // Upload with 'site' category so it doesn't appear in gallery
      const url = await uploadImage(preparedFile, {
        category: 'site',
        onProgress: setUploadProgress,
      });

      // Set as selected image
      setSelectedImage(url);
      setSelectedImageTitle(file.name);
      setUploadPreview(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(message);
      setUploadPreview(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Filter media by search query
  const filteredMedia = searchQuery
    ? media.filter(m =>
        m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : media;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Images className="w-5 h-5" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'gallery' | 'upload' | 'url')} className="flex-1 flex flex-col min-h-0 px-6">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="gallery" className="flex items-center gap-2">
              <Images className="w-4 h-4" />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="url" className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              URL
            </TabsTrigger>
          </TabsList>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
            {/* Main content area with preview */}
            <div className="flex-1 flex gap-4 min-h-0">
              {/* Left side: Album/Image browser */}
              <div className="flex-1 flex flex-col min-h-0 min-w-0">
                {/* Navigation breadcrumb */}
                <div className="flex items-center gap-2 mb-3">
                  {breadcrumbTrail.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleBack}>
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>
                  )}
                  <span className="text-sm text-muted-foreground truncate">
                    {currentAlbum ? currentAlbum.name : 'All Albums'}
                  </span>
                </div>

                {/* Search */}
                {media.length > 0 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search images..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                )}

                {/* Scrollable content area */}
                <ScrollArea className="flex-1 -mx-1 px-1">
                  <div className="pb-4">
                    {loading ? (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <Skeleton key={i} className="aspect-square rounded-lg" />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Albums */}
                        {albums.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium mb-2">Albums</h4>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                              {albums.map((album) => (
                                <button
                                  key={album.id}
                                  onClick={() => handleAlbumClick(album)}
                                  className="group relative aspect-square rounded-lg overflow-hidden border bg-muted hover:ring-2 hover:ring-primary transition-all"
                                >
                                  {album.coverUrl ? (
                                    <Image
                                      src={album.coverUrl}
                                      alt={album.name}
                                      fill
                                      className="object-cover"
                                    />
                                  ) : (
                                    <div className="flex items-center justify-center h-full">
                                      <Folder className="w-10 h-10 text-muted-foreground/50" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/50 flex items-end p-2">
                                    <span className="text-white text-xs font-medium truncate w-full">
                                      {album.name}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Images */}
                        {filteredMedia.length > 0 && (
                          <div>
                            {albums.length > 0 && (
                              <h4 className="text-sm font-medium mb-2">Images</h4>
                            )}
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                              {filteredMedia.map((item) => (
                                <button
                                  key={item.id}
                                  onClick={() => handleImageSelect(item.url, item.title)}
                                  className={`group relative aspect-square rounded-lg overflow-hidden border transition-all ${
                                    selectedImage === item.url
                                      ? 'ring-2 ring-primary ring-offset-2'
                                      : 'hover:ring-2 hover:ring-primary/50'
                                  }`}
                                >
                                  <Image
                                    src={item.thumbnailUrl || item.url}
                                    alt={item.title}
                                    fill
                                    className="object-cover"
                                  />
                                  {selectedImage === item.url && (
                                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                      <div className="bg-primary text-primary-foreground rounded-full p-1.5">
                                        <Check className="w-4 h-4" />
                                      </div>
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="absolute bottom-0 left-0 right-0 p-1.5">
                                      <span className="text-white text-xs truncate block">
                                        {item.title}
                                      </span>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Empty state */}
                        {!loading && albums.length === 0 && filteredMedia.length === 0 && (
                          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                            <Images className="w-12 h-12 mb-4 opacity-50" />
                            <p className="text-sm">No images found</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>

              {/* Right side: Preview panel */}
              <div className="w-72 flex-shrink-0 flex flex-col border-l pl-4">
                <h4 className="text-sm font-medium mb-3">Preview</h4>
                {selectedImage ? (
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="relative flex-1 rounded-lg overflow-hidden border bg-muted min-h-[200px]">
                      <Image
                        src={selectedImage}
                        alt={selectedImageTitle || 'Selected image'}
                        fill
                        className="object-contain"
                      />
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7"
                        onClick={handleClearSelection}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {selectedImageTitle && (
                      <p className="mt-2 text-sm font-medium truncate">{selectedImageTitle}</p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground break-all line-clamp-2">
                      {selectedImage}
                    </p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 text-muted-foreground">
                    <Images className="w-10 h-10 mb-2 opacity-50" />
                    <p className="text-sm">No image selected</p>
                    <p className="text-xs mt-1">Click an image to preview</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
            <div className="flex-1 flex gap-4">
              {/* Left side: Upload area */}
              <div className="flex-1 flex flex-col">
                <div
                  className={`flex-1 flex flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
                    uploading
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-muted-foreground/50 cursor-pointer'
                  }`}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={handleDrop}
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-4 p-8">
                      <Loader2 className="w-12 h-12 animate-spin text-primary" />
                      <p className="text-sm font-medium">Uploading...</p>
                      <Progress value={uploadProgress} className="w-48" />
                      <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
                    </div>
                  ) : uploadPreview ? (
                    <div className="relative w-full h-full p-4">
                      <img
                        src={uploadPreview}
                        alt="Upload preview"
                        className="w-full h-full object-contain rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-4 p-8">
                      <div className="rounded-full bg-muted p-4">
                        <Upload className="w-10 h-10 text-muted-foreground" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">Drop an image here, or click to browse</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PNG, JPG, GIF, WebP up to 10MB
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />

                {uploadError && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>{uploadError}</AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Right side: Preview panel */}
              <div className="w-72 flex-shrink-0 flex flex-col border-l pl-4">
                <h4 className="text-sm font-medium mb-3">Uploaded Image</h4>
                {selectedImage && tab === 'upload' ? (
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="relative flex-1 rounded-lg overflow-hidden border bg-muted min-h-[200px]">
                      <Image
                        src={selectedImage}
                        alt={selectedImageTitle || 'Uploaded image'}
                        fill
                        className="object-contain"
                      />
                      <Button
                        variant="secondary"
                        size="icon"
                        className="absolute top-2 right-2 h-7 w-7"
                        onClick={handleClearSelection}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {selectedImageTitle && (
                      <p className="mt-2 text-sm font-medium truncate">{selectedImageTitle}</p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground break-all line-clamp-2">
                      {selectedImage}
                    </p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 text-muted-foreground">
                    <Upload className="w-10 h-10 mb-2 opacity-50" />
                    <p className="text-sm">No image uploaded</p>
                    <p className="text-xs mt-1 text-center px-4">
                      Upload an image to see preview
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* URL Tab */}
          <TabsContent value="url" className="flex-1 flex flex-col min-h-0 mt-0 data-[state=inactive]:hidden">
            <div className="flex-1 flex gap-4">
              <div className="flex-1 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Image URL</label>
                  <Input
                    placeholder="https://example.com/image.jpg"
                    value={urlInput}
                    onChange={(e) => {
                      setUrlInput(e.target.value);
                      setSelectedImage(e.target.value);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Enter a direct URL to an image (JPG, PNG, WebP, etc.)
                  </p>
                </div>
              </div>

              {/* Preview panel for URL tab */}
              <div className="w-72 flex-shrink-0 flex flex-col border-l pl-4">
                <h4 className="text-sm font-medium mb-3">Preview</h4>
                {urlInput ? (
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="relative flex-1 rounded-lg overflow-hidden border bg-muted min-h-[200px]">
                      <Image
                        src={urlInput}
                        alt="Preview"
                        fill
                        className="object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground break-all line-clamp-3">
                      {urlInput}
                    </p>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/50 text-muted-foreground">
                    <LinkIcon className="w-10 h-10 mb-2 opacity-50" />
                    <p className="text-sm">No URL entered</p>
                    <p className="text-xs mt-1">Enter a URL to preview</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/30">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedImage && !urlInput}
          >
            <Check className="w-4 h-4 mr-2" />
            Select Image
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
